/**
 * Reporta "backend fora do ar em horário comercial" como evento no
 * Sentry, com fingerprint estável pra dedup e tag dedicada.
 *
 * Configure uma alert rule no Sentry para enviar email quando issues
 * com tag `backend.status=down` aparecem ou são reabertas. O Sentry
 * lida com throttle nativamente via fingerprint (mesma "issue" só
 * notifica em transições).
 *
 * Em cima disso, mantemos um throttle local de 1h via localStorage
 * pra evitar inundar o Sentry quando o usuário ficar dando refresh.
 *
 * Nota arquitetural: a primeira ideia foi expor um endpoint público
 * da Lambda `optimus-scheduler` (action=health-alert) — implementado e
 * pronto no back. Mas a conta AWS tem alguma policy de governança que
 * bloqueia AUTH_TYPE=NONE em Function URLs (todas as tentativas
 * retornaram 403 antes do Lambda runtime, incluindo numa Lambda
 * hello-world isolada). Pivotamos para Sentry para destravar a feature
 * sem depender de resolver o bloqueio da AWS.
 */

import { captureMessage } from '../lib/sentry'

const STORAGE_KEY = 'mdi:backend-down-alert:last-sent-at'
const LOCAL_THROTTLE_MS = 60 * 60 * 1000 // 1h

export interface AlertParams {
  source: string // ex.: 'signin', 'dashboard'
}

function shouldSendAlert(): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return true
    const last = Number.parseInt(raw, 10)
    if (!Number.isFinite(last)) return true
    return Date.now() - last >= LOCAL_THROTTLE_MS
  } catch {
    return true
  }
}

function markSent() {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()))
  } catch {
    /* localStorage indisponível — segue o jogo */
  }
}

export function reportBackendDown(params: AlertParams): void {
  if (!shouldSendAlert()) return
  markSent()

  // Mensagem com fingerprint estável para o Sentry dedupar como uma
  // única issue. Variar `source` na mensagem mas manter o fingerprint
  // fixo agrupa todos os clientes reportando o mesmo evento.
  captureMessage(
    `[backend-down] /health/live falhou em horário comercial (source=${params.source})`,
    'error'
  )
}
